/* eslint-disable @typescript-eslint/ban-ts-comment */
import {DefaultDraftBlockRenderMap, EditorState, Modifier} from 'draft-js';
import {stateToHTML} from 'draft-js-export-html';
import {stateFromHTML} from 'draft-js-import-html';
import {Map} from 'immutable';
import {v4 as idGenerator} from 'uuid';
import {useState} from 'react';
import {useMobile} from '../useMobile';

// #FFDE7C = secondary-lighter-yellow
const DEFAULT_HIGHLIGHT_COLOR = '#FFDE7C';

export const MARK_OPTIONS = {
  openingTag: '<mark class="marking">',
  closingTag: '</mark>',
  fullTag: '<mark class="marking"></mark>',
  regex: /<mark class="marking">([\s\S]*?)<\/mark>/g, // Captures any content inside the <mark> tag, including line breaks and nested HTML
  // [\s\S]*? -> matches all characters (spaces, line breaks, and tags) in a non-greedy way
};

export interface Highlight {
  withNestedMarks?: string;
  withoutNestedMarks: string;
  index: number;
}

interface MarkTagPositions {
  openingMark: {
    startIndex: number;
    endIndex: number;
  };
  closingMark: {
    startIndex: number;
    endIndex: number;
  };
}

interface useDraftAsTextHighlighterProps {
  highlightColor?: string;
  onHighlightChange?: (
    highlightedHTML: string,
    HighlightListCount: number,
  ) => void;
}

function useDraftAsTextHighlighter({
  highlightColor = DEFAULT_HIGHLIGHT_COLOR,
  onHighlightChange,
}: useDraftAsTextHighlighterProps = {}) {
  const [highlightedHTML, setHighlightedHTML] = useState('');
  const [highlightEnabled, setHighlightEnabled] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [highlightList, setHighlightList] = useState<Highlight[] | undefined>();
  const {isMobile} = useMobile();

  const highlighterBlockRenderMap = DefaultDraftBlockRenderMap.merge(
    Map({mark: {element: 'mark'}}),
  );

  const highlighterStyleMap = {
    marking: {backgroundColor: highlightColor},
  };

  function convertHTMLStringToDraftState(HTMLString: string) {
    const newEditorState = EditorState.createWithContent(
      stateFromHTML(HTMLString, {
        customInlineFn: ({tagName}, {Style}) => {
          if (tagName === 'MARK') {
            return Style(`marking`);
          }
          return null;
        },
        customBlockFn: (block) => {
          if (block.nodeName === 'FIGURE') {
            const imageNode = block.children[0];
            return {
              type: 'atomic',
              data: {src: imageNode.getAttribute('src')},
            };
          }
          return {};
        },
      }),
    );

    setEditorState(newEditorState);
    setHighlightedHTML(HTMLString);
    updateHighlightList(newEditorState);
  }

  function convertDraftStateToHTMLString(editorState?: EditorState) {
    if (!editorState) return '';

    // Defines export of common inline styles to HTML
    const options = {
      inlineStyles: {
        BOLD: {element: 'strong'},
        ITALIC: {element: 'em'},
        UNDERLINE: {element: 'u'},
      },
      inlineStyleFn: (styles) => {
        // Defines how the "marking" style should be exported to HTML
        if (styles.has('marking')) {
          return {
            element: 'mark',
            attributes: {class: 'marking'},
          };
        }
        return null;
      },
      blockRenderers: {
        atomic: (block) => {
          return `<figure><img src="${block.getData().get('src')}" /></figure>`;
        },
      },
      entityStyleFn: (entity) => {
        if (entity?.get('type') === 'IMAGE') {
          return {
            element: 'img',
            attributes: {src: entity?.get('data').src},
          };
        }
        return;
      },
    };

    // Converts the current editor content to HTML using the defined options
    return stateToHTML(editorState.getCurrentContent(), options);
  }

  function getMarkTagPositions(HTMLNode: string): MarkTagPositions {
    const openingMarkTagLength = MARK_OPTIONS.openingTag.length;
    const closingMarkTagLength = MARK_OPTIONS.closingTag.length;

    const nodeFirstTagLength = HTMLNode.match(/<.*?>/)?.[0].length || 0;
    const nodeClosingTagLength = nodeFirstTagLength + 1; // plus one for the closing slash

    const openingMarkStartIndex = nodeFirstTagLength;
    const openingMarkEndIndex = nodeFirstTagLength + openingMarkTagLength;

    const closingMarkStartIndex =
      HTMLNode.length - (closingMarkTagLength + nodeClosingTagLength);
    const closingMarkEndIndex = HTMLNode.length - nodeClosingTagLength;

    return {
      openingMark: {
        startIndex: openingMarkStartIndex,
        endIndex: openingMarkEndIndex,
      },
      closingMark: {
        startIndex: closingMarkStartIndex,
        endIndex: closingMarkEndIndex,
      },
    };
  }

  function handleNestedHTMLMarks(
    HTMLString: string,
    openingMarkPointer?: string,
    closingMarkPointer?: string,
  ) {
    const HTMLNodeArray = HTMLString.split('\n');

    return HTMLNodeArray.reduce(
      (HTMLNodeArrayWithoutNestedMarks, currentHTMLNode) => {
        const previousHTMLNode = HTMLNodeArrayWithoutNestedMarks.slice(-1)[0];

        const {openingMark: currentNodeOpeningMark} =
          getMarkTagPositions(currentHTMLNode);
        const {closingMark: previousNodeClosingMark} =
          getMarkTagPositions(previousHTMLNode);

        const isCurrentNodeStartingWithOpeningMark =
          currentHTMLNode.substring(
            currentNodeOpeningMark.startIndex,
            currentNodeOpeningMark.endIndex,
          ) === MARK_OPTIONS.openingTag;

        const isPreviousNodeEndingWithClosingMark =
          previousHTMLNode.substring(
            previousNodeClosingMark.startIndex,
            previousNodeClosingMark.endIndex,
          ) === MARK_OPTIONS.closingTag;

        if (
          isPreviousNodeEndingWithClosingMark &&
          isCurrentNodeStartingWithOpeningMark
        ) {
          const HTMLNodeArrayWithoutLastNode =
            HTMLNodeArrayWithoutNestedMarks.slice(0, -1);

          const HTMLNodeArrayLastNodeWithoutClosingMark =
            HTMLNodeArrayWithoutNestedMarks.slice(-1)[0].replace(
              MARK_OPTIONS.closingTag,
              closingMarkPointer ?? '',
            );

          const currentHTMLNodeWithoutOpeningMark = currentHTMLNode.replace(
            MARK_OPTIONS.openingTag,
            openingMarkPointer ?? '',
          );

          return [
            ...HTMLNodeArrayWithoutLastNode,
            HTMLNodeArrayLastNodeWithoutClosingMark,
            currentHTMLNodeWithoutOpeningMark,
          ];
        }
        // Case condition is not attended we assume that the current html hasn't nested marks.
        return [...HTMLNodeArrayWithoutNestedMarks, currentHTMLNode];
      },
      [''],
    );
  }

  function getHighlightList(HTMLString: string): Highlight[] | undefined {
    const openingMarkPointer = idGenerator();
    const closingMarkPointer = idGenerator();
    const HTMLWithMarkPointers = handleNestedHTMLMarks(
      HTMLString,
      openingMarkPointer,
      closingMarkPointer,
    ).join('');

    const HTMLWithoutNestedMarks = handleNestedHTMLMarks(HTMLString).join('');

    const highlightListWithoutNestedMarks = HTMLWithoutNestedMarks.match(
      MARK_OPTIONS.regex,
    );

    const highlightListWithMarkPointers =
      HTMLWithMarkPointers.match(MARK_OPTIONS.regex) || [];

    const highlightList = highlightListWithoutNestedMarks?.map(
      (highlight, index) => {
        const withNestedMarks = highlightListWithMarkPointers[index]
          ?.replace(
            // couldn't use replaceAll beacause of the JEST version
            new RegExp(openingMarkPointer, 'g'),
            MARK_OPTIONS.openingTag,
          )
          ?.replace(
            new RegExp(closingMarkPointer, 'g'),
            MARK_OPTIONS.closingTag,
          );

        return {
          withoutNestedMarks: highlight,
          withNestedMarks,
          index,
        };
      },
    );
    return highlightList;
  }

  function deleteHighlight(highlightToDelete: Highlight) {
    const HTMLNodeArray = highlightedHTML.split('\n');
    const HTMLWithoutNestedMarks =
      handleNestedHTMLMarks(highlightedHTML).join('');

    const highlightListWithNestedMarks =
      highlightedHTML.match(MARK_OPTIONS.regex) || [];

    const highlightListWithoutNestedMarks = HTMLWithoutNestedMarks.match(
      MARK_OPTIONS.regex,
    );

    /* Verifies the difference between Nested and Unnested higlight lists,
     * if a node with the same index on both lists is different,
     * we assume that is a multi node highlight. */
    const multiNodeHighlightList = highlightListWithNestedMarks.filter(
      (item) => highlightListWithoutNestedMarks?.indexOf(item) === -1,
    );

    let modifiedHTMLString = '';

    const isDeleteTargetMultiNodeHighlight = multiNodeHighlightList.some(
      (item) => highlightToDelete.withNestedMarks?.includes(item),
    );

    if (isDeleteTargetMultiNodeHighlight) {
      modifiedHTMLString = multiNodeHighlightList
        .reduce((clearedHTML, multiNodeHighlight) => {
          // Remove mark from withoutNestedMarks
          return clearedHTML.map((HTMLNode) => {
            if (
              HTMLNode.includes(multiNodeHighlight) &&
              highlightToDelete.withNestedMarks?.includes(multiNodeHighlight)
            ) {
              const modifiedNode = HTMLNode.replace(
                MARK_OPTIONS.openingTag,
                '',
              ).replace(MARK_OPTIONS.closingTag, '');

              return modifiedNode;
            }
            return HTMLNode;
          });
        }, HTMLNodeArray)
        .join('\n');
    } else {
      const cleanHighlightToDelete = highlightToDelete.withoutNestedMarks
        .replace(MARK_OPTIONS.openingTag, '')
        .replace(MARK_OPTIONS.closingTag, '');

      modifiedHTMLString = highlightedHTML.replace(
        highlightToDelete.withoutNestedMarks,
        cleanHighlightToDelete,
      );
    }

    convertHTMLStringToDraftState(modifiedHTMLString);
    const updatedHighlightList = getHighlightList(modifiedHTMLString);

    onHighlightChange?.(modifiedHTMLString, updatedHighlightList?.length ?? 0);
  }

  function addHighlight(editorState?: EditorState) {
    if (!highlightEnabled || !editorState) return;

    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    // Guard: Ensure selection is valid and not collapsed (has actual text selected)
    // This prevents TypeError when Modifier.applyInlineStyle tries to apply style
    // to an empty or invalid selection range
    if (!selection || !currentContent || selection.isCollapsed()) {
      // IMPORTANT: Update editor state even when not applying highlight
      // This keeps the internal state in sync with Draft.js
      setEditorState(editorState);
      return;
    }

    // Applies the "marking" style to the current selection in the content
    const newContent = Modifier.applyInlineStyle(
      currentContent,
      selection,
      'marking',
    );

    // Creates a new EditorState from the modified content,
    // preserving the original state and adding the marking
    const highlightedEditorState = EditorState.push(
      editorState,
      newContent,
      'change-inline-style',
    );

    const highlightedHTML = convertDraftStateToHTMLString(
      highlightedEditorState,
    );

    if (!isMobile) {
      document.getSelection()?.empty();
    }

    setEditorState(highlightedEditorState);
    setHighlightedHTML(highlightedHTML);

    const updatedHighlightList = updateHighlightList(highlightedEditorState);

    onHighlightChange?.(highlightedHTML, updatedHighlightList?.length ?? 0);
  }

  function updateHighlightList(newEditorState: EditorState) {
    if (!newEditorState) {
      setHighlightList([]);
      return [];
    }

    const highlights = getHighlightList(
      convertDraftStateToHTMLString(newEditorState),
    );

    setHighlightList(highlights);
    return highlights;
  }

  function toggleHighlightEnabled() {
    setHighlightEnabled((prev) => !prev);
  }

  return {
    convertHTMLStringToDraftState,
    convertDraftStateToHTMLString,
    getHighlightList,
    deleteHighlight,
    addHighlight,
    blockRenderMap: highlighterBlockRenderMap,
    styleMap: highlighterStyleMap,
    highlightedHTML,
    editorState,
    highlightList,
    highlightEnabled,
    toggleHighlightEnabled,
  };
}

export default useDraftAsTextHighlighter;
